import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { shareToWhatsApp } from "@/lib/share";
import { toast } from "sonner";
import { StatusBadge, formatDateTime } from "@/lib/status";
import { ShoppingCart, ChevronRight, MessageCircle, Mail, RefreshCw, Globe } from "lucide-react";
import { Button } from "@/components/ui/button";
import MailComposeDialog from "@/components/MailComposeDialog";
import B2BOrderDialog from "@/components/B2BOrderDialog";
export default function PurchasingInboxPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/inbox/purchasing");
      setItems(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="fade-up">
      <div className="flex items-end justify-between mb-5 gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center">
            <ShoppingCart className="h-5 w-5" />
          </div>
          <div>
            <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">Satın Alma Kutusu</h1>
            <p className="text-sm text-slate-500 mt-0.5">
              <span data-testid="purchasing-count" className="font-medium text-slate-900">{items.length}</span> talep işlem bekliyor
            </p>
          </div>
        </div>
        <Button variant="outline" data-testid="refresh-btn" onClick={load} className="h-10 gap-2">
          <RefreshCw className="h-4 w-4" /> Yenile
        </Button>
      </div>

      {loading ? (
        <div className="text-center text-slate-400 py-12">Yükleniyor...</div>
      ) : items.length === 0 ? (
        <div data-testid="empty-purchasing" className="text-center py-16 border border-dashed border-slate-200 rounded-lg bg-white">
          <ShoppingCart className="h-10 w-10 mx-auto text-slate-300 mb-2" />
          <div className="text-slate-700 font-medium">Şu an işlem bekleyen talep yok</div>
          <div className="text-sm text-slate-400 mt-1">Kullanıcılar talep ilettiğinde burada görüneceksiniz.</div>
        </div>
      ) : (
        <div className="space-y-2.5">
          {items.map((r) => (
            <RequestCard key={r.id} r={r} onChanged={load} />
          ))}
        </div>
      )}
    </div>
  );
}

function RequestCard({ r, onChanged }) {
  const [mailOpen, setMailOpen] = useState(false);
  const [b2bOpen, setB2bOpen] = useState(false);
  const items = (r.items && r.items.length > 0) ? r.items : [{
    product_code: r.product_code, product_name: r.product_name,
    quantity: r.quantity, unit: r.unit,
  }];
  const totalQty = items.reduce((s, i) => s + (Number(i.quantity) || 0), 0);

  const waNumber = (r.supplier_phone || "").replace(/[^\d]/g, "");
  const itemListText = items.map((it, i) => `${i + 1}. ${it.product_code} — ${it.product_name} • ${it.quantity} ${it.unit}`).join("\n");
  const waMessage = `Merhaba ${r.supplier_name},\n\n*${r.request_no}* numaralı sipariş talebimiz:\n${itemListText}\n\nDetaylar ekteki PDF'tedir.`;
  const waUrl = waNumber
    ? `https://wa.me/${waNumber}?text=${encodeURIComponent(waMessage)}`
    : `https://wa.me/?text=${encodeURIComponent(waMessage)}`;

  const fetchPdfBlob = async () => {
    const { data } = await api.get(`/requests/${r.id}/pdf`, { responseType: "blob" });
    return new File([data], `talep-${r.request_no}.pdf`, { type: "application/pdf" });
  };

  const markSent = async (channel) => {
    try {
      await api.post(`/requests/${r.id}/mark-sent`, {
        channel, supplier_id: r.supplier_id, supplier_name: r.supplier_name,
      });
    } catch (_) {}
  };

  const shareWhatsApp = async () => {
    let file = null;
    try { file = await fetchPdfBlob(); } catch (_) { /* text only */ }
    const res = await shareToWhatsApp({
      text: waMessage,
      title: `Talep ${r.request_no}`,
      file,
      waUrl,
      downloadName: `talep-${r.request_no}.pdf`,
    });
    if (res === "cancelled") return;
    toast.success(res === "waurl" ? "WhatsApp açıldı" : "Paylaşım menüsü açıldı");
    await markSent("whatsapp");
    onChanged?.();
  };

  return (
    <div data-testid={`purchasing-row-${r.id}`} className="bg-white border border-slate-200 rounded-lg p-4 sm:p-5">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Link to={`/talepler/${r.id}`} className="font-display font-semibold text-slate-900 hover:underline">
              {r.request_no}
            </Link>
            <StatusBadge status={r.status} />
          </div>
          <div className="text-xs text-slate-500 mt-1">
            {formatDateTime(r.created_at)} • {r.user_name}
          </div>
        </div>
        <div className="text-right">
          <div className="text-xs text-slate-500">Tedarikçi</div>
          <div className="text-sm font-medium text-slate-900">{r.supplier_name}</div>
          {r.supplier_phone && <div className="text-xs text-slate-400">{r.supplier_phone}</div>}
        </div>
      </div>

      {/* Items summary */}
      <div className="border border-slate-100 rounded-md overflow-hidden mb-3 max-h-44 overflow-y-auto thin-scroll">
        {items.map((it, idx) => (
          <div key={idx} className="grid grid-cols-12 gap-2 px-3 py-2 border-t border-slate-100 first:border-t-0 items-center text-sm">
            <div className="col-span-1 text-xs text-slate-400">{idx + 1}</div>
            <div className="col-span-3 font-medium text-slate-900 text-xs">{it.product_code}</div>
            <div className="col-span-5 text-slate-700 text-xs truncate">{it.product_name}</div>
            <div className="col-span-2 text-right font-medium text-slate-900">{it.quantity}</div>
            <div className="col-span-1 text-xs text-slate-500">{it.unit}</div>
          </div>
        ))}
      </div>

      {r.purchasing_note && (
        <div className="text-xs bg-amber-50 border border-amber-200 text-amber-900 rounded-md p-2.5 mb-3">
          <span className="font-medium">Kullanıcı notu:</span> {r.purchasing_note}
        </div>
      )}

      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-xs text-slate-500">
          {items.length} kalem · Toplam {totalQty} adet/birim
        </div>
        <div className="flex gap-2 flex-wrap">
          <Link to={`/talepler/${r.id}`}>
            <Button variant="outline" size="sm" className="h-9 gap-1.5">
              Detay <ChevronRight className="h-3.5 w-3.5" />
            </Button>
          </Link>
          <Button
            data-testid={`b2b-${r.id}`}
            variant="outline"
            size="sm"
            className="h-9 gap-1.5 border-indigo-200 text-indigo-700 hover:bg-indigo-50"
            onClick={() => setB2bOpen(true)}
          >
            <Globe className="h-3.5 w-3.5" /> B2B
          </Button>
          <Button
            data-testid={`mail-${r.id}`}
            variant="outline"
            size="sm"
            className="h-9 gap-1.5 border-blue-200 text-blue-700 hover:bg-blue-50"
            onClick={() => setMailOpen(true)}
          >
            <Mail className="h-3.5 w-3.5" /> Mail
          </Button>
          <Button
            data-testid={`wa-${r.id}`}
            size="sm"
            className="h-9 gap-1.5 bg-[#25D366] hover:bg-[#128C7E] text-white"
            onClick={shareWhatsApp}
          >
            <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
          </Button>
        </div>
      </div>

      <MailComposeDialog open={mailOpen} onOpenChange={setMailOpen} request={r} onSent={onChanged} />
      <B2BOrderDialog open={b2bOpen} onOpenChange={setB2bOpen} request={r} onDone={onChanged} />
    </div>
  );
}
